/**
 * Settings for customizing Artwork PDF print output.
 * Structure matches backend pdf.dto.ts
 */
export type ArtworkPrintSettings = {
  // ============================================
  // GLOBAL OPTIONS
  // ============================================
  hideDate: boolean;

  // ============================================
  // BLOC: DESCRIPTION (Identification)
  // ============================================
  showArtist: boolean;
  showTitle: boolean;
  oeuvresDescriptionSousTitre: boolean;
  oeuvresDescriptionAutreTitre: boolean;
  oeuvresDescriptionReference: boolean;
  showCreationDate: boolean;
  showCreationPlace: boolean;
  showRegistrationNumber: boolean;
  showInventoryNumber: boolean;
  showCollectionRef: boolean;
  oeuvresDescriptionEnsemble: boolean;

  // ============================================
  // BLOC: TECHNIQUE / SUPPORT
  // ============================================
  showTechnique: boolean;
  showSupport: boolean;
  showDimensions: boolean;
  hideDimensionLabel: boolean;
  hideInches: boolean;
  showSignature: boolean;
  showInscriptionsBack: boolean;
  showDoubleFace: boolean;

  // ============================================
  // BLOC: REFERENCE (Classification)
  // ============================================
  showCategory: boolean;
  showTheme: boolean;
  showSeries: boolean;
  showMovement: boolean;
  showGeographicArea: boolean;
  showPeriod: boolean;

  // ============================================
  // BLOC: NOTES
  // ============================================
  oeuvresValidationNotesDeTravail: boolean;
  oeuvresValidationRemarquesPubliques: boolean;
  showPersonalNotes: boolean;

  // ============================================
  // BLOC: DIMENSIONS / POIDS (Extended)
  // ============================================
  showWeight: boolean;
  showDimensionsHorsMarges: boolean;
  showFrameDimensions: boolean;
  showSurfaceVolume: boolean;
  showCustomDimensions: boolean;

  // ============================================
  // BLOC: LOCALISATION / HISTORIQUE
  // ============================================
  showCurrentCollection: boolean;
  forceCollectorName: boolean;
  showNotLocated: boolean;
  oeuvresLocalisationHistorique: boolean;
  oeuvresLocalisationHistoriqueLieuDeStockage: boolean;

  // ============================================
  // BLOC: SUIVI FINANCIER (Financial Tracking)
  // ============================================
  showEstimationDescription: boolean;
  showEstimationDate: boolean;
  showEstimationAmount: boolean;
  showInsuranceValue: boolean;
  showInsuranceDate: boolean;
  showInsuranceNotes: boolean;
  showSaleDescription: boolean;
  showSaleDate: boolean;
  showSaleAmount: boolean;
  showSaleVAT: boolean;
  showSaleFees: boolean;
  showAcquisitionDescription: boolean;
  showAcquisitionDate: boolean;
  showAcquisitionAmount: boolean;
  showAcquisitionVAT: boolean;
  showAcquisitionCustomsDuties: boolean;
  showAcquisitionResaleRights: boolean;
  showAcquisitionSaleFees: boolean;
  showProduction: boolean;
  showTransport: boolean;
  showRestoration: boolean;
  showFraming: boolean;
  showPhoto: boolean;
  showExpertise: boolean;
  showSuiviFinancierNotes: boolean;

  // ============================================
  // BLOC: RANGEMENT (Storage/Inventory)
  // ============================================
  showStorageLocation: boolean;
  showStorageRoom: boolean;
  showStorageRow: boolean;
  showStorageRack: boolean;
  showStorageFurniture: boolean;
  showStorageShelf: boolean;
  showStorageBox: boolean;
  showStorageBinder: boolean;
  showStorageOtherRef: boolean;
  showStorageDate: boolean;

  // ============================================
  // BLOC: CONDITIONS (Conservation)
  // ============================================
  oeuvresRegieNotesCondition: boolean;
  showRestored: boolean;
  showToRestore: boolean;

  // ============================================
  // BLOC: DOCUMENTS ADMINISTRATIFS
  // ============================================
  oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs: boolean;

  // ============================================
  // BLOC: CREDITS
  // ============================================
  showCreditsArtwork: boolean;
  showCreditsPhoto: boolean;

  // ============================================
  // BLOC: RELATIONS (Linked Entities)
  // ============================================
  relatedArtworks: boolean;
  oeuvresDansExposition: boolean;
  oeuvresDansBibliographie: boolean;
  oeuvresDansVentes: boolean;
  relatedAudiovisuals: boolean;
  relatedDocuments: boolean;
  relatedWritings: boolean;
  relatedPhotos: boolean;
  relatedMultipleprints: boolean;

  // ============================================
  // BLOC: DESCRIPTION (extended — added fields)
  // ============================================
  showOtherNumber: boolean;
  showFormerNumber: boolean;
  showRating: boolean;
  showStatus: boolean;
  showSummary: boolean;
  showKeywords: boolean;
  showFormerTitle: boolean;
  showArtworkType: boolean;
  showSupportRemark: boolean;
  showManufacturing: boolean;
  showLongDescription: boolean;
  showDigitalFileName: boolean;

  // ============================================
  // BLOC: LOCALISATION / HISTORIQUE (extended)
  // ============================================
  showCollectionCatalogueName: boolean;
  showCollectionNote: boolean;
  showDepositDate: boolean;
  showDepositReturnDate: boolean;
  showDepositNote: boolean;

  // ============================================
  // BLOC: REGIE (Frame / Crate)
  // ============================================
  showFrameDescription: boolean;
  showRegieNotes: boolean;
  showCrateDescription: boolean;
  showCrateDimensions: boolean;
  showCrateWeight: boolean;
  showCrateWeightWithArtwork: boolean;

  // ============================================
  // BLOC: RANGEMENT / RECOLEMENT (extended)
  // ============================================
  showStorageNotes: boolean;
  showMarkingDate: boolean;
  showRecolementOperator: boolean;
  showRecolementDate: boolean;
  showRecolementCampaign: boolean;
  showRecolementOtherRef: boolean;
  showRecolementNotes: boolean;

  // ============================================
  // BLOC: CREDITS / DROITS (extended) + SOURCES
  // ============================================
  showRightsNature: boolean;
  showRightsAmount: boolean;
  showRightsRemark: boolean;
  showSources: boolean;

  // ============================================
  // BLOC: SECONDARY IMAGES
  // ============================================
  /** Show secondary/annex images in PDF output */
  showSecondaryImages: boolean;
};

// Type for print templates
export type PrintTemplate = "simple" | "complete" | "binder";

/**
 * Field groups for the "Options d'affichage" tab.
 * Organized to match the Figma mockup layout (2-column flat display).
 *
 * LEFT COLUMN:  description, dimensionsPoids, localisationHistorique
 * RIGHT COLUMN: suiviFinancier, rangement, autres
 *
 * The `column` property controls which column a group appears in.
 */
export const PRINT_SETTINGS_GROUPS = {
  infosDeBase: {
    key: "infosDeBase",
    titleKey: "printSettingsInfosDeBase",
    column: "left" as const,
    fields: [
      "showArtist",
      "showTitle",
      "showCreationDate",
      "showRegistrationNumber",
      "showTechnique",
      "showDimensions",
      "hideDimensionLabel",
      "showSignature",
      "showInscriptionsBack",
      "showEstimationAmount",
    ] as const,
  },
  description: {
    key: "description",
    titleKey: "printSettingsDescription",
    column: "left" as const,
    fields: [
      "showCreationPlace",
      "showInventoryNumber",
      "showCollectionRef",
      "hideInches",
      "showDoubleFace",
      "oeuvresDescriptionSousTitre",
      "oeuvresDescriptionAutreTitre",
      "showOtherNumber",
      "showFormerNumber",
      "showFormerTitle",
      "showArtworkType",
      "showSupportRemark",
      "showManufacturing",
      "showSummary",
      "showLongDescription",
      "showKeywords",
      "showRating",
      "showDigitalFileName",
    ] as const,
  },
  sansSection: {
    key: "sansSection",
    titleKey: "printSettingsSansSection",
    column: "left" as const,
    fields: [
      "oeuvresDescriptionReference",
      "showCategory",
      "showTheme",
      "showSeries",
      "showMovement",
      "showGeographicArea",
      "showPeriod",
      "showStatus",
      "oeuvresValidationNotesDeTravail",
      "oeuvresValidationRemarquesPubliques",
      "showPersonalNotes",
    ] as const,
  },
  dimensionsPoids: {
    key: "dimensionsPoids",
    titleKey: "printSettingsDimensionsPoids",
    column: "left" as const,
    fields: [
      "showSurfaceVolume",
      "showCustomDimensions",
      "showWeight",
      "showFrameDimensions",
      "showDimensionsHorsMarges",
    ] as const,
  },
  localisationHistorique: {
    key: "localisationHistorique",
    titleKey: "printSettingsLocalisationHistorique",
    column: "left" as const,
    fields: [
      "showCurrentCollection",
      "showNotLocated",
      "oeuvresLocalisationHistorique",
      "showCollectionCatalogueName",
      "showCollectionNote",
      "showDepositDate",
      "showDepositReturnDate",
      "showDepositNote",
    ] as const,
  },
  regie: {
    key: "regie",
    titleKey: "printSettingsRegie",
    column: "left" as const,
    fields: [
      "showFrameDescription",
      "showRegieNotes",
      "showCrateDescription",
      "showCrateDimensions",
      "showCrateWeight",
      "showCrateWeightWithArtwork",
    ] as const,
  },
  suiviFinancier: {
    key: "suiviFinancier",
    titleKey: "printSettingsSuiviFinancier",
    column: "right" as const,
    fields: [
      "showEstimationDescription",
      "showEstimationDate",
      "showInsuranceValue",
      "showInsuranceDate",
      "showInsuranceNotes",
      "showSaleDescription",
      "showSaleDate",
      "showSaleAmount",
      "showSaleVAT",
      "showSaleFees",
      "showAcquisitionDescription",
      "showAcquisitionDate",
      "showAcquisitionAmount",
      "showAcquisitionVAT",
      "showAcquisitionCustomsDuties",
      "showAcquisitionResaleRights",
      "showAcquisitionSaleFees",
      "showProduction",
      "showTransport",
      "showRestoration",
      "showFraming",
      "showPhoto",
      "showExpertise",
      "showSuiviFinancierNotes",
    ] as const,
  },
  rangement: {
    key: "rangement",
    titleKey: "printSettingsRangement",
    column: "right" as const,
    fields: [
      "showStorageLocation",
      "showStorageRoom",
      "showStorageRow",
      "showStorageRack",
      "showStorageFurniture",
      "showStorageShelf",
      "showStorageBox",
      "showStorageBinder",
      "showStorageOtherRef",
      "showStorageDate",
      "showStorageNotes",
      "showMarkingDate",
      "showRecolementOperator",
      "showRecolementDate",
      "showRecolementCampaign",
      "showRecolementOtherRef",
      "showRecolementNotes",
    ] as const,
  },
  autres: {
    key: "autres",
    titleKey: "printSettingsAutres",
    column: "right" as const,
    fields: [
      "hideDate",
      "oeuvresDescriptionEnsemble",
      "showCreditsArtwork",
      "showCreditsPhoto",
      "showRightsNature",
      "showRightsAmount",
      "showRightsRemark",
      "oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs",
      "oeuvresRegieNotesCondition",
      "showRestored",
      "showToRestore",
      "showSources",
    ] as const,
  },
  relations: {
    key: "relations",
    titleKey: "printSettingsRelations",
    column: "right" as const,
    fields: [
      "relatedArtworks",
      "oeuvresDansExposition",
      "oeuvresDansBibliographie",
      "oeuvresDansVentes",
      "relatedAudiovisuals",
      "relatedDocuments",
      "relatedWritings",
      "relatedPhotos",
      "relatedMultipleprints",
    ] as const,
  },
} as const;

/**
 * Binder-specific field groups matching the ODS spec for "Fiche Classeur".
 * The binder has a different grouping structure than Simple/Complete.
 */
export const BINDER_SETTINGS_GROUPS = {
  description: {
    key: "description",
    titleKey: "printSettingsDescription",
    column: "left" as const,
    fields: [
      "oeuvresDescriptionEnsemble",
      "oeuvresDescriptionSousTitre",
      "oeuvresDescriptionAutreTitre",
      "oeuvresDescriptionReference",
      "showCreationDate",
      "showCreationPlace",
      "showRegistrationNumber",
      "showTechnique",
      "showDimensions",
      "hideInches",
      "showSignature",
      "showInscriptionsBack",
      "showCurrentCollection",
      "showStatus",
      "showLongDescription",
      "hideDate",
    ] as const,
  },
  suiviFinancier: {
    key: "suiviFinancier",
    titleKey: "printSettingsSuiviFinancier",
    column: "right" as const,
    fields: ["showEstimationAmount"] as const,
  },
  rangement: {
    key: "rangement",
    titleKey: "printSettingsRangement",
    column: "right" as const,
    fields: ["showStorageLocation"] as const,
  },
  sansSection: {
    key: "sansSection",
    titleKey: "printSettingsSansSection",
    column: "right" as const,
    fields: ["oeuvresValidationRemarquesPubliques"] as const,
  },
} as const;

type SettingsGroups = typeof PRINT_SETTINGS_GROUPS | typeof BINDER_SETTINGS_GROUPS;

/**
 * Returns the appropriate settings groups for a given template.
 * Binder has its own group structure; Simple/Complete share the default groups.
 */
export function getSettingsGroups(template: PrintTemplate): SettingsGroups {
  if (template === "binder") return BINDER_SETTINGS_GROUPS;
  return PRINT_SETTINGS_GROUPS;
}

// Template-specific field arrays
// Binder (Classeur): all fields checked in artwork_binder.pug templates
export const binderTemplateFields = [
  // Description (matching ODS spec for Fiche Classeur)
  "oeuvresDescriptionEnsemble",
  "oeuvresDescriptionSousTitre",
  "oeuvresDescriptionAutreTitre",
  "oeuvresDescriptionReference",
  "showCreationDate",
  "showCreationPlace",
  "showRegistrationNumber",
  "showTechnique",
  "showDimensions",
  "hideInches",
  "showSignature",
  "showInscriptionsBack",
  "showCurrentCollection",
  "hideDate",
  // Champs ajoutés au Classeur (retour client) : présents dans Petite Photo
  "showStatus",
  "showLongDescription",
  "showEstimationAmount",
  "showStorageLocation",
  "oeuvresValidationRemarquesPubliques",
] as const;

// Simple (Fiche Simple): all fields from Excel spec
export const simpleTemplateFields = [
  // Description
  "showArtist",
  "showTitle",
  "oeuvresDescriptionSousTitre",
  "oeuvresDescriptionAutreTitre",
  "showCreationDate",
  "showCreationPlace",
  "showRegistrationNumber",
  "showInventoryNumber",
  "showCollectionRef",
  "showTechnique",
  "showDimensions",
  "hideDimensionLabel",
  "hideInches",
  "showSignature",
  "showInscriptionsBack",
  "showDoubleFace",
  "oeuvresDescriptionReference",
  "showCategory",
  "showTheme",
  "showSeries",
  "showMovement",
  "showGeographicArea",
  "showPeriod",
  "oeuvresValidationNotesDeTravail",
  "oeuvresValidationRemarquesPubliques",
  "showPersonalNotes",
  // Dimensions et poids
  "showDimensionsHorsMarges",
  "showWeight",
  "showFrameDimensions",
  "showSurfaceVolume",
  "showCustomDimensions",
  // Localisation - Historique
  "showCurrentCollection",
  "showNotLocated",
  "oeuvresLocalisationHistorique",
  "oeuvresLocalisationHistoriqueLieuDeStockage",
  // Suivi Financier
  "showEstimationDescription",
  "showEstimationDate",
  "showEstimationAmount",
  "showInsuranceValue",
  "showInsuranceDate",
  "showInsuranceNotes",
  "showSaleDescription",
  "showSaleDate",
  "showSaleAmount",
  "showSaleVAT",
  "showSaleFees",
  "showAcquisitionDescription",
  "showAcquisitionDate",
  "showAcquisitionAmount",
  "showAcquisitionVAT",
  "showAcquisitionCustomsDuties",
  "showAcquisitionResaleRights",
  "showAcquisitionSaleFees",
  // Rangement
  "showStorageLocation",
  "showStorageRoom",
  "showStorageRow",
  "showStorageRack",
  "showStorageFurniture",
  "showStorageShelf",
  "showStorageBox",
  "showStorageBinder",
  "showStorageOtherRef",
  "showStorageDate",
  // Autres
  "hideDate",
  "oeuvresDescriptionEnsemble",
  "showCreditsArtwork",
  "showCreditsPhoto",
  "oeuvresDocumentsAdministratifsNotesDocumentsAdministratifs",
  "oeuvresRegieNotesCondition",
  "showRestored",
  "showToRestore",
  // Description (extended)
  "showOtherNumber",
  "showFormerNumber",
  "showFormerTitle",
  "showArtworkType",
  "showSupportRemark",
  "showManufacturing",
  "showSummary",
  "showLongDescription",
  "showKeywords",
  "showRating",
  "showStatus",
  "showDigitalFileName",
  // Localisation / historique (extended)
  "showCollectionCatalogueName",
  "showCollectionNote",
  "showDepositDate",
  "showDepositReturnDate",
  "showDepositNote",
  // Régie (cadre / caisse)
  "showFrameDescription",
  "showRegieNotes",
  "showCrateDescription",
  "showCrateDimensions",
  "showCrateWeight",
  "showCrateWeightWithArtwork",
  // Rangement / récolement (extended)
  "showStorageNotes",
  "showMarkingDate",
  "showRecolementOperator",
  "showRecolementDate",
  "showRecolementCampaign",
  "showRecolementOtherRef",
  "showRecolementNotes",
  // Crédits / droits (extended) + sources
  "showRightsNature",
  "showRightsAmount",
  "showRightsRemark",
  "showSources",
] as const;

// Complete (Fiche Complete): all of Simple + relations + extended financial rubrics
export const completeTemplateFields = [
  ...simpleTemplateFields,
  // Extended Suivi Financier (Complete only — see #2132611)
  "showProduction",
  "showTransport",
  "showRestoration",
  "showFraming",
  "showPhoto",
  "showExpertise",
  "showSuiviFinancierNotes",
  // Relations (Complete only)
  "oeuvresDansExposition",
  "oeuvresDansBibliographie",
  "oeuvresDansVentes",
  "relatedArtworks",
  "relatedAudiovisuals",
  "relatedDocuments",
  "relatedWritings",
  "relatedPhotos",
  "relatedMultipleprints",
] as const;

// Helper to get fields for a template and filter by group
export function getFieldsForTemplate(
  template: PrintTemplate,
  groupKey: string,
): readonly string[] {
  const templateFields =
    template === "simple"
      ? simpleTemplateFields
      : template === "complete"
        ? completeTemplateFields
        : binderTemplateFields;

  const groups = getSettingsGroups(template);
  const group = (groups as Record<string, { fields: readonly string[] }>)[groupKey];
  if (!group) return [];

  return group.fields.filter((field) =>
    templateFields.includes(field as never),
  );
}

// Get all settings fields initialized to false
export function getAllSettingsFields(): ArtworkPrintSettings {
  const fromGroups = Object.values(PRINT_SETTINGS_GROUPS)
    .flatMap((group) => group.fields)
    .reduce(
      (acc, field) => ({ ...acc, [field]: false }),
      {} as Record<string, boolean>,
    );

  // Include fields that exist in the type but may not be in display groups
  return {
    ...fromGroups,
    showCreationPlace: false,
    showSupport: false,
    showMovement: false,
    showGeographicArea: false,
    showPeriod: false,
    forceCollectorName: false,
    showSecondaryImages: false,
    oeuvresLocalisationHistoriqueLieuDeStockage: false,
  } as ArtworkPrintSettings;
}
